/**
 * Inspector V2 pane geometry.
 *
 * The inspector renders into `#babylon-inspector-container` as an *overlay* on
 * top of the full-width canvas — the canvas is never resized, so the panes sit
 * over it:
 *
 *   [ left pane | transparent central column | right pane ]
 *
 * Anything that must stay clear of the panes measures that flex row: the
 * bottom-docked overlays (clip editor, pose/keyframe/state strips) to set their
 * own left/right, and the viewer's safe area so in-scene GUI like AutoUI knows
 * which slice of the canvas is actually visible.
 *
 * Finding the row is a moving target, which is why this keeps checking rather
 * than measuring once:
 *
 *  - `ShowInspector()` returns before React has rendered the shell, so an
 *    immediate measurement finds nothing at all.
 *  - React's first paints put up rows that are then replaced, so the first
 *    element matching the shape can be a transient one that is detached
 *    moments later — latching onto it looks like success and then never
 *    updates again.
 *  - The inspector re-initializes on scene load, replacing the whole shell.
 *
 * Measuring once is what left the safe area permanently zero and put the AutoUI
 * panel underneath the left pane.
 */

/** Widths of the inspector's left and right panes, in CSS pixels. */
export interface PaneInsets {
    left: number;
    right: number;
}

/** How often to re-check that the row we are measuring is still the real one. */
const RECHECK_MS = 500;

/**
 * Watch the inspector's pane widths and report them whenever they change.
 *
 * `onChange` fires as soon as the panes are mounted and laid out, and again on
 * every change to their widths (drag, collapse, window resize, inspector
 * re-init). Repeat reports of unchanged values are suppressed.
 *
 * @returns a dispose function; safe to call whether or not the panes ever showed up.
 */
export function observeInspectorPaneInsets(onChange: (insets: PaneInsets) => void): () => void {
    let resizeObserver: ResizeObserver | null = null;
    let tracked: HTMLElement | null = null;
    let last: PaneInsets | null = null;

    /** The outer flex row holding [leftPane, central, rightPane]. */
    function findFlexRow(): HTMLElement | null {
        const wrapper = document.getElementById('viewer-wrapper');
        const container = wrapper?.querySelector('#babylon-inspector-container');
        if (!container) return null;

        for (const row of container.querySelectorAll<HTMLElement>('div')) {
            if (!isFlexRow(row)) continue;
            return row;
        }
        return null;
    }

    function isFlexRow(row: HTMLElement): boolean {
        if (row.children.length < 2) return false;
        // Zero width means "not laid out yet" (or a row React is about to
        // discard) — either way it is not something to measure against.
        if (row.offsetWidth <= 0) return false;
        return getComputedStyle(row).flexDirection === 'row';
    }

    function measure(row: HTMLElement): void {
        const children = row.children;
        const first = children[0] as HTMLElement | undefined;
        const lastChild = children[children.length - 1] as HTMLElement | undefined;
        if (!first || !lastChild) return;

        // A single child is the central column alone — no pane to avoid.
        const insets: PaneInsets = {
            left: first.offsetWidth > 0 ? first.offsetWidth : 0,
            right: (lastChild !== first && lastChild.offsetWidth > 0) ? lastChild.offsetWidth : 0,
        };

        if (last && last.left === insets.left && last.right === insets.right) return;
        last = insets;
        onChange(insets);
    }

    function track(row: HTMLElement): void {
        resizeObserver?.disconnect();
        tracked = row;
        // The ResizeObserver is what makes a pane drag feel live; the interval
        // below is what makes finding the row in the first place reliable.
        resizeObserver = new ResizeObserver(() => {
            if (tracked) measure(tracked);
        });
        resizeObserver.observe(row);
        for (const child of row.children) {
            resizeObserver.observe(child);
        }
        measure(row);
    }

    function recheck(): void {
        // Cheap path: the row we are on is still the live one, just re-measure.
        if (tracked?.isConnected && isFlexRow(tracked)) {
            measure(tracked);
            return;
        }
        const row = findFlexRow();
        if (row) track(row);
    }

    recheck();
    const timer = setInterval(recheck, RECHECK_MS);

    return () => {
        clearInterval(timer);
        resizeObserver?.disconnect();
        resizeObserver = null;
        tracked = null;
    };
}
